import { Link } from "react-router-dom";
import { ArrowRight, Gauge, Cpu, Network, Truck, Phone } from "lucide-react";
import { useFetch } from "@/hooks/useFetch";
import { Seo } from "@/components/site/Seo";
import { Img, SectionLabel } from "@/components/site/Img";
import { Reveal } from "@/components/site/Reveal";
import { Markdown } from "@/components/site/Markdown";
import { ProductCard } from "@/components/site/ProductCard";
import { PortalButton } from "@/components/site/Header";
import { PHONE, PHONE_HREF } from "@/lib/api";

const PILLAR_STYLE = [[Gauge, "ᚦ"], [Cpu, "ᛏ"], [Network, "ᛉ"], [Truck, "ᚱ"]];
const pillars = (page) => (page?.blocks || []).map((b, i) => ({ icon: PILLAR_STYLE[i % 4][0], rune: PILLAR_STYLE[i % 4][1], title: b.title, text: b.text, to: b.url || "/" }));
const BRANDS = [["alientech", "Alientech", "KESS3"], ["autotuner", "Autotuner", "OBD · Bench"], ["dimsport", "Dimsport", "Genius · Trasdata"], ["flex", "Magic Motorsport", "FLEX"], ["cmd", "CMD", "Flashtec"]];

function Hero({ page }) {
  return (
    <section className="grain relative isolate min-h-[88vh] overflow-hidden border-b border-[color:var(--line)]" data-testid="home-hero">
      {page?.hero_image && <Img src={page.hero_image.url} alt={page.hero_image.alt} eager sizes="100vw" className="absolute inset-0 -z-10 h-full w-full object-cover opacity-55" />}
      <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#0b0d10] via-[#0b0d10]/80 to-transparent" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-t from-[#0b0d10] via-transparent to-[#0b0d10]/40" />
      <div className="knot-bg absolute inset-0 -z-10 opacity-[0.035]" />
      <div className="pointer-events-none absolute inset-y-0 right-[8%] hidden w-px bg-gradient-to-b from-transparent via-[color:var(--ice)]/30 to-transparent lg:block" />
      <div className="container-x flex min-h-[88vh] flex-col justify-end pb-16 pt-24 lg:pb-24">
        <div className="max-w-4xl">
          <div className="rise"><SectionLabel rune="ᛃ">{page?.eyebrow || "Skipton · North Yorkshire"}</SectionLabel></div>
          <h1 className="rise mt-6 font-display text-[2.6rem] font-bold uppercase leading-[0.92] tracking-tight text-white sm:text-6xl lg:text-[5.5rem]" style={{ animationDelay: "120ms" }} data-testid="page-h1">
            {page?.h1 || "ECU tuning tools, files and dealer support from people who tune"}
          </h1>
          <p className="rise mt-7 max-w-2xl text-base leading-relaxed text-slate-300 md:text-lg" style={{ animationDelay: "240ms" }}>{page?.intro}</p>
          <div className="rise mt-10 flex flex-wrap gap-3" style={{ animationDelay: "360ms" }}>
            <Link to="/shop" className="btn-ice" data-testid="hero-shop-button">Shop tuning tools <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/remappingfranchise" className="btn-ghost" data-testid="hero-dealer-button">Become a dealer</Link>
          </div>
        </div>
        <div className="rise mt-16 grid max-w-3xl grid-cols-2 gap-px bg-[color:var(--line)] sm:grid-cols-3" style={{ animationDelay: "480ms" }}>
          {[["20+ years", "Engine management tuning"], ["4WD dyno", "In-house file development"], ["5 brands", "Master & slave tools"]].map(([a, b]) => (
            <div key={a} className="bg-[#0b0d10]/85 p-5 backdrop-blur-md">
              <p className="font-display text-2xl font-semibold uppercase text-white">{a}</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">{b}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const { data: page } = useFetch("/pages/home");
  const { data: featured } = useFetch("/products", { featured: true, limit: 8 });
  const { data: banners } = useFetch("/banners", { placement: "home_strip" });
  const { data: posts } = useFetch("/posts", { limit: 3 });
  return (
    <div data-testid="home-page">
      <Seo path="/" />
      <Hero page={page} />

      <section className="container-x py-24">
        <div className="grid gap-px bg-[color:var(--line)] sm:grid-cols-2 lg:grid-cols-4">
          {pillars(page).map((p, i) => (
            <Reveal key={p.title} delay={i * 90}>
              <Link to={p.to} className="group flex h-full flex-col bg-[color:var(--bg)] p-8 transition-colors hover:bg-[color:var(--bg-2)]" data-testid={`home-pillar-${i}`}>
                <div className="flex items-center justify-between">
                  <p.icon className="h-6 w-6 text-[color:var(--ice)]" strokeWidth={1.4} />
                  <span className="font-mono text-sm text-[color:var(--bronze)]/70">{p.rune}</span>
                </div>
                <h2 className="mt-10 font-display text-2xl font-semibold uppercase text-white">{p.title}</h2>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{p.text}</p>
                <ArrowRight className="mt-8 h-4 w-4 text-slate-500 transition-all group-hover:translate-x-1 group-hover:text-[color:var(--ice)]" />
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-y border-[color:var(--line)] bg-[color:var(--bg-2)] py-24">
        <div className="container-x">
          <Reveal className="mb-12 flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <div>
              <SectionLabel rune="ᛏ" index="02">Featured tools</SectionLabel>
              <h2 className="mt-4 font-display text-3xl font-bold uppercase text-white sm:text-4xl lg:text-5xl">Tools in stock</h2>
            </div>
            <Link to="/shop" className="btn-ghost self-start" data-testid="home-view-all-products">View all products <ArrowRight className="h-4 w-4" /></Link>
          </Reveal>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {(featured || []).slice(0, 8).map((p, i) => <Reveal key={p.id} delay={(i % 4) * 80}><ProductCard p={p} /></Reveal>)}
          </div>
        </div>
      </section>

      <section className="container-x grid gap-16 py-24 lg:grid-cols-[1fr_1.1fr] lg:items-center">
        <Reveal className="relative">
          <div className="chamfer relative aspect-[4/3] overflow-hidden">
            <div className="absolute inset-0 bg-[color:var(--bg-3)]" />
            <ToolsImage />
          </div>
          <div className="absolute -bottom-5 -right-3 hidden border border-[color:var(--bronze)]/50 bg-[#0b0d10] px-5 py-4 sm:block">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[color:var(--bronze)]">Cars · Vans · HGV · Agri · Bikes · Marine</p>
          </div>
        </Reveal>
        <Reveal delay={120}>
          <SectionLabel rune="ᚢ" index="03" bronze>About Jorvik Tuning</SectionLabel>
          <Markdown className="prose-jt mt-6" testId="home-body">{page?.body}</Markdown>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/about" className="btn-ghost" data-testid="home-about-link">About us</Link>
            <Link to="/ecu-remapping" className="btn-ghost" data-testid="home-ecu-link">How remapping works</Link>
          </div>
        </Reveal>
      </section>

      <section className="border-y border-[color:var(--line)] py-16">
        <div className="container-x">
          <p className="eyebrow mb-8">Brands we supply</p>
          <div className="grid grid-cols-2 gap-px bg-[color:var(--line)] sm:grid-cols-3 lg:grid-cols-5">
            {BRANDS.map(([slug, a, b]) => (
              <Link key={slug} to={`/tuning-tools/${slug}`} className="group bg-[color:var(--bg)] p-6 transition-colors hover:bg-[color:var(--bg-2)]" data-testid={`home-brand-${slug}`}>
                <p className="font-display text-xl font-semibold uppercase tracking-wide text-slate-200 group-hover:text-white">{a}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 group-hover:text-[color:var(--ice)]">{b}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {(banners || []).map((b) => (
        <section key={b.id} className="container-x py-16" data-testid="home-banner">
          <Reveal className="relative overflow-hidden border border-[color:var(--bronze)]/40 bg-gradient-to-r from-[#17130f] to-[color:var(--bg-2)] p-8 sm:p-12">
            <div className="knot-bg absolute inset-0 opacity-[0.04]" />
            <div className="relative flex flex-col justify-between gap-6 md:flex-row md:items-center">
              <div>
                <h2 className="font-display text-3xl font-bold uppercase text-white">{b.title}</h2>
                <p className="mt-2 text-slate-400">{b.text}</p>
              </div>
              <div className="flex flex-wrap gap-3">
                {b.link_url && <Link to={b.link_url} className="btn-ice" data-testid="home-banner-cta">{b.link_label || "Find out more"}</Link>}
                <a href={PHONE_HREF} className="btn-ghost"><Phone className="h-4 w-4" />{PHONE}</a>
              </div>
            </div>
          </Reveal>
        </section>
      ))}

      {posts?.length > 0 && (
        <section className="container-x py-16">
          <Reveal className="mb-10 flex items-end justify-between">
            <div><SectionLabel rune="ᛗ" index="04">Guides & News</SectionLabel><h2 className="mt-4 font-display text-3xl font-bold uppercase sm:text-4xl">Latest from Jorvik</h2></div>
            <Link to="/blog" className="hidden text-sm text-[color:var(--ice)] hover:underline sm:block" data-testid="home-all-posts">All news</Link>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-3">
            {posts.map((p, i) => (
              <Reveal key={p.id} delay={i * 90}>
                <Link to={`/blog/post/${p.slug}`} className="group block" data-testid={`home-post-${p.slug}`}>
                  <div className="aspect-[16/10] overflow-hidden bg-[color:var(--bg-3)]">{p.cover_image && <Img src={p.cover_image.url} alt={p.cover_image.alt} sizes="(max-width:768px) 100vw, 33vw" className="h-full w-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-105" />}</div>
                  <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">{new Date(p.published_at).toLocaleDateString("en-GB", { month: "short", year: "numeric" })}</p>
                  <h3 className="mt-2 font-display text-xl font-semibold uppercase text-slate-100 group-hover:text-[color:var(--ice)]">{p.title}</h3>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      <section className="container-x pt-8">
        <Reveal className="grid gap-8 border-t border-[color:var(--line)] pt-16 md:grid-cols-[1.4fr_1fr] md:items-center">
          <div>
            <h2 className="font-display text-3xl font-bold uppercase sm:text-4xl">Already a Jorvik dealer?</h2>
            <p className="mt-3 max-w-xl text-slate-400">Upload reads, download files and manage credits in the customer portal.</p>
          </div>
          <div className="md:justify-self-end"><PortalButton className="!px-6 !py-3.5 !text-sm" testId="home-portal-cta" /></div>
        </Reveal>
      </section>
    </div>
  );
}

function ToolsImage() {
  const { data } = useFetch("/pages/tuning-tools");
  return data?.hero_image ? <Img src={data.hero_image.url} alt={data.hero_image.alt} className="absolute inset-0 h-full w-full object-cover" /> : null;
}
